import { ChevronLeft, ChevronRight, CalendarDays, AlertTriangle, PackageX } from "lucide-react";
import { STATUS_STYLES } from "@/lib/constants";
import { DOWNTIME_BG, OFFDAY_BG, sameDay, isOffDay } from "./ganttUtils";

const VIEW_MODES = [
  { days: 1, label: "Day" },
  { days: 3, label: "3-Day" },
  { days: 7, label: "Week" },
];

export function GanttToolbar({ canDrag, days, onSetDays, onMove, onToday }) {
  return (
    <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
      <div className="flex items-center gap-2">
        <CalendarDays className="h-5 w-5 text-blue-600" />
        <div>
          <h3 className="font-display text-base font-semibold text-slate-900">Production Schedule</h3>
          <p className="text-xs text-slate-400">
            Bars span estimated build time · {canDrag ? "drag to reschedule across lines & days" : "read-only schedule view"}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex rounded-lg border border-slate-200 overflow-hidden" data-testid="gantt-view-mode-toggle">
          {VIEW_MODES.map((m) => (
            <button
              key={m.days}
              onClick={() => onSetDays(m.days)}
              className={`px-3 py-1.5 text-xs font-medium ${days === m.days ? "bg-blue-600 text-white" : "text-slate-500 hover:bg-slate-50"}`}
            >
              {m.label}
            </button>
          ))}
        </div>
        <button onClick={() => onMove(-1)} className="h-8 w-8 rounded-md border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50"><ChevronLeft className="h-4 w-4" /></button>
        <button onClick={onToday} className="px-3 h-8 rounded-md border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50">Today</button>
        <button onClick={() => onMove(1)} className="h-8 w-8 rounded-md border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50"><ChevronRight className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

export function GanttHeaderRow({ cols, days, workingDays }) {
  return (
    <div className="grid border-b border-slate-100" style={{ gridTemplateColumns: `176px repeat(${days}, 1fr)` }}>
      <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-slate-400 font-semibold bg-slate-50">Line / Machine</div>
      {cols.map((d) => {
        const today = sameDay(d, new Date());
        const off = isOffDay(d, workingDays);
        return (
          <div key={d.toISOString()} className={`px-3 py-2 text-center border-l border-slate-100 ${today ? "bg-blue-50" : "bg-slate-50"}`}>
            <div className={`text-[10px] uppercase font-semibold ${off ? "text-slate-300" : "text-slate-400"}`}>
              {d.toLocaleDateString(undefined, { weekday: "short" })}{off ? " ·off" : ""}
            </div>
            <div className={`text-sm font-mono-data font-semibold ${today ? "text-blue-600" : off ? "text-slate-400" : "text-slate-700"}`}>{d.getDate()}</div>
          </div>
        );
      })}
    </div>
  );
}

export function GanttLegend() {
  return (
    <div className="px-5 py-2.5 border-t border-slate-100 flex items-center gap-4 text-[11px] text-slate-500 flex-wrap">
      {["Completed", "In Progress", "Delayed", "Pending"].map((st) => (
        <span key={st} className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ background: STATUS_STYLES[st].bar }} />
          {st}
        </span>
      ))}
      <span className="flex items-center gap-1.5">
        <AlertTriangle className="h-3 w-3 text-amber-500" />
        Capacity conflict (downtime overlap)
      </span>
      <span className="flex items-center gap-1.5">
        <span className="flex items-center justify-center h-3.5 w-3.5 rounded-full bg-red-600"><PackageX className="h-2.5 w-2.5 text-white" /></span>
        Material shortage (stock can't cover remaining units)
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-4 rounded-sm border border-red-300" style={{ background: DOWNTIME_BG }} />
        Downtime window
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-4 rounded-sm border border-slate-200" style={{ background: OFFDAY_BG }} />
        Non-working day
      </span>
      <span className="flex items-center gap-1.5">
        <span className="relative h-2.5 w-4 rounded-sm bg-slate-300"><span className="absolute bottom-0 left-0 h-[2px] w-2/3 bg-white" /></span>
        Production progress
      </span>
    </div>
  );
}
